import {IconButton, Menu, MenuItem} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LogoutIcon from "@mui/icons-material/Logout";
import {useState} from "react";
import {useNavigate} from "react-router";
import {useTranslation} from "react-i18next";
import {useAuth} from "react-oidc-context";
import {store} from "../../../store";
import {authReducerActions} from "../../../reducer/authReducer.ts";
import {saveSupportContext} from "../../../utils/supportContextStorage.ts";
import {useRoutesByTag} from "../../../service/routeProvider.tsx";
import useDirtyLeaveCheck from "../../../hooks/useDirtyLeaveCheck.ts";
import {useAppSelector} from "../../../hooks/reduxHooks.ts";
import {type MyAccount} from "../../../model/user/Account.ts";

interface SidebarUserCardProps {
    myAccount: MyAccount;
    collapsed?: boolean;
}

const SidebarUserCard = ({myAccount, collapsed = false}: SidebarUserCardProps) => {
    const navigate = useNavigate();
    const auth = useAuth();
    const {t} = useTranslation();
    const {denyDirtyLeave, dialog: confirmDialog} = useDirtyLeaveCheck();
    const agreementFaulty = useAppSelector(state => state.auth.agreementFaulty);
    const dpaRoutes = useRoutesByTag("DPA");

    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const closeMenu = () => { setMenuAnchor(null); };

    const goProfile = async () => {
        closeMenu();
        if (await denyDirtyLeave()) return;
        if (agreementFaulty && dpaRoutes.length === 1) {
            void navigate(dpaRoutes[0].path);
            return;
        }
        void navigate("/user");
    };

    const handleLogout = () => {
        closeMenu();
        void auth.signoutRedirect({id_token_hint: auth.user?.id_token});
        void auth.clearStaleState();
        saveSupportContext(null);
        store.dispatch(authReducerActions.logoutSuccess());
    };

    if (collapsed) {
        return (
            <div className="mt-auto flex flex-col items-center gap-2">
                <IconButton
                    aria-label={myAccount.userDetails.name}
                    onClick={() => { void goProfile(); }}
                    sx={{width: 48, height: 48}}
                >
                    <AccountCircleIcon sx={{color: "var(--an-muted-2)"}}/>
                </IconButton>
                <IconButton aria-label="Logout" onClick={handleLogout} sx={{width: 48, height: 48}}>
                    <LogoutIcon sx={{color: "var(--an-muted-2)"}}/>
                </IconButton>
                {confirmDialog}
            </div>
        );
    }

    return (
        <>
            <div
                // Default 48px chamfer eats into this card's 76px-tall content
                // box (clips avatar's top-left and menu icon's bottom-right).
                // 36px chamfer preserves the brand silhouette without intruding.
                className="mt-auto an-shape an-shape-shadow [--an-shape-cut:36px] bg-an-surface px-10 py-3 grid items-center gap-3"
                style={{gridTemplateColumns: "24px 1fr 24px"}}
            >
                <button
                    type="button"
                    onClick={() => { void goProfile(); }}
                    className="block bg-transparent border-0 p-0 cursor-pointer rounded-full items-center justify-center flex"
                    aria-label={myAccount.userDetails.name}
                >
                    <AccountCircleIcon sx={{width: 28, height: 60, color: "var(--an-muted-2)"}}/>
                </button>
                <div className="min-w-0 leading-tight">
                    <div className="font-lato text-sm font-medium text-an-ink truncate">
                        {myAccount.userDetails.name}
                    </div>
                    <div className="font-lato text-xs text-an-muted-4 truncate">
                        {myAccount.userDetails.email}
                    </div>
                </div>
                <IconButton
                    aria-label="More"
                    onClick={e => { setMenuAnchor(e.currentTarget); }}
                    // 40px hit target (WCAG 2.5.8); negative margin keeps the
                    // 24px grid column so the visual layout stays unchanged.
                    sx={{width: 40, height: 40, m: "-8px"}}
                >
                    <MoreVertIcon sx={{fontSize: 20, color: "var(--an-ink)"}}/>
                </IconButton>
            </div>
            <Menu
                anchorEl={menuAnchor}
                open={!!menuAnchor}
                onClose={closeMenu}
                slotProps={{paper: {sx: {minWidth: "auto", width: "auto"}}}}
            >
                <MenuItem onClick={() => { void goProfile(); }}>
                    <AccountCircleIcon fontSize="small" sx={{mr: 1}}/>
                    {t("defaults.profile")}
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                    <LogoutIcon fontSize="small" sx={{mr: 1}}/>
                    {t("defaults.logout")}
                </MenuItem>
            </Menu>
            {confirmDialog}
        </>
    );
};

export default SidebarUserCard;
